import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Mail, MapPin, MessageCircle } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { whatsappLink, WHATSAPP_NUMBER } from "../../lib/whatsapp";

// Premium 5-column footer — inspired by Linear / Stripe / Vercel
// Avoids duplicated links, gives proper visual hierarchy, and keeps the
// brand mark + tagline anchored on the left.
const Col = ({ title, children }) => (
  <div>
    <h4 className="text-[11px] font-mono tracking-[0.22em] uppercase text-white/45 mb-5">
      {title}
    </h4>
    <ul className="space-y-3 text-sm">{children}</ul>
  </div>
);

const Item = ({ to, href, children, testid }) => {
  const base =
    "text-white/65 hover:text-white transition-colors duration-200 inline-flex items-center";
  if (to) {
    return (
      <li>
        <Link to={to} data-testid={testid} className={base}>
          {children}
        </Link>
      </li>
    );
  }
  return (
    <li>
      <a href={href} data-testid={testid} className={base}>
        {children}
      </a>
    </li>
  );
};

export const Footer = () => {
  const { t } = useI18n();
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative mt-24 border-t border-white/[0.06] bg-[#05050A]"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20">
        {/* Top grid — 5 columns on lg+ */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-x-8 gap-y-12">
          {/* Column 1 — Brand block (spans wider) */}
          <div className="col-span-2 md:col-span-3 lg:col-span-4">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/jf-logo.png"
                srcSet="/jf-logo-192.png 1x, /jf-logo.png 2x"
                alt="Joshi FusionTech Media"
                width="44"
                height="44"
                className="w-11 h-11"
                loading="lazy"
                decoding="async"
              />
              <div className="leading-tight">
                <div className="font-display font-extrabold text-white tracking-tight text-[15px]">
                  JOSHI <span className="text-brand-orange">FUSIONTECH</span> MEDIA
                </div>
                <div className="font-mono text-[10px] tracking-[0.28em] text-white/45 uppercase mt-1">
                  {t.footer.tagline}
                </div>
              </div>
            </Link>
            <p className="mt-6 text-sm text-white/60 max-w-sm leading-relaxed">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-3 mt-7">
              <a
                href="https://www.linkedin.com/company/joshifusiontech"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-white/65 hover:text-white hover:border-brand-orange/40 hover:scale-105 transition-all duration-200"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/joshifusiontech/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-white/65 hover:text-white hover:border-brand-orange/40 hover:scale-105 transition-all duration-200"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="mailto:info@joshifusiontech.de"
                aria-label="Email"
                data-testid="social-email"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-white/65 hover:text-white hover:border-brand-orange/40 hover:scale-105 transition-all duration-200"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Column 2 — Services */}
          <div className="lg:col-span-2">
            <Col title={t.footer.services}>
              <Item to="/services/ai-automation">{t.footer.svc_ai}</Item>
              <Item to="/services/web-development">{t.footer.svc_web}</Item>
              <Item to="/services/digital-marketing">{t.footer.svc_marketing}</Item>
              <Item to="/services/content-branding">{t.footer.svc_content}</Item>
              <Item to="/services/brand-design">{t.footer.svc_brand}</Item>
              <Item to="/services/strategy-consulting">{t.footer.svc_strategy}</Item>
            </Col>
          </div>

          {/* Column 3 — Company */}
          <div className="lg:col-span-2">
            <Col title={t.footer.company}>
              <Item to="/about" testid="footer-about">{t.nav.about}</Item>
              <Item to="/portfolio" testid="footer-portfolio">{t.footer.references}</Item>
              <Item to="/contact" testid="footer-careers">{t.footer.careers}</Item>
              <Item href="#" testid="footer-blog">{t.footer.blog}</Item>
              <Item to="/contact" testid="footer-contact">{t.nav.contact}</Item>
            </Col>
          </div>

          {/* Column 4 — Legal */}
          <div className="lg:col-span-1">
            <Col title={t.footer.legal}>
              <Item to="/legal/impressum" testid="footer-impressum">{t.footer.impressum}</Item>
              <Item to="/legal/datenschutz" testid="footer-privacy">{t.footer.privacy}</Item>
              <Item to="/legal/agb" testid="footer-terms">{t.footer.terms}</Item>
              <Item to="/login" testid="footer-login">{`Login`}</Item>
            </Col>
          </div>

          {/* Column 5 — Contact — wider column so the email fits on one line */}
          <div className="lg:col-span-3">
            <Col title={t.footer.contact}>
              <li className="flex items-start gap-2.5 text-white/65">
                <MapPin className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" />
                <span>{t.footer.address}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-brand-orange shrink-0 mt-1" />
                <a
                  href="mailto:info@joshifusiontech.de"
                  data-testid="footer-email"
                  className="contact-email text-[13.5px] md:text-sm text-white/70 hover:text-white transition-colors"
                >
                  info@joshifusiontech.de
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MessageCircle className="w-4 h-4 text-[#25D366] shrink-0 mt-1" />
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="contact-email text-[13.5px] md:text-sm text-white/70 hover:text-white transition-colors"
                >
                  WhatsApp Business
                </a>
              </li>
            </Col>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-16 mb-8 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs font-mono text-white/40">
          <p>© {year} Joshi FusionTech Media. {t.footer.rights}</p>
          <p className="tracking-[0.18em] uppercase">
            <span>Automate.</span>{" "}
            <span className="text-brand-orange">Innovate.</span>{" "}
            <span>Elevate.</span>
          </p>
        </div>
      </div>
    </footer>
  );
};
